/**
 * Number announcements in the browser.
 *
 * Two constraints shape this, both absent on Android:
 *
 *  1. **Autoplay is blocked until the user gestures.** Mobile Safari and Chrome
 *     both refuse to play audio that was not started from a user interaction.
 *     The Join button is that gesture, so [unlock] is called from it — playing
 *     a silent clip to move the audio element into an allowed state.
 *
 *  2. **Clips are fetched, not bundled.** They are served from the same origin
 *     and cached by the browser, so the first call of a game may lag slightly
 *     while the clip downloads. Pre-fetching every clip up front would mean a
 *     2 MB download before play starts, which is worse on a phone connection.
 */

export type Language = 'en' | 'hi' | 'te';

const CLIP_BASE = '/audio';

export class Announcer {
  private element: HTMLAudioElement | null = null;
  private lastSpoken: number | null = null;
  private unlocked = false;

  language: Language = 'en';
  enabled = true;

  /**
   * Must be called from a real user gesture (a click or tap), or every
   * subsequent play() is rejected by the browser.
   */
  unlock(): void {
    if (this.unlocked) return;
    this.element = new Audio();
    this.element.preload = 'auto';
    // Playing and immediately pausing marks the element as user-activated.
    this.element.muted = true;
    void this.element
      .play()
      .then(() => {
        this.element?.pause();
        if (this.element) this.element.muted = false;
        this.unlocked = true;
      })
      .catch(() => {
        // Some browsers reject the silent play; the first real announcement
        // will still work if the gesture was genuine.
        this.unlocked = true;
      });
  }

  /**
   * Speaks a number once.
   *
   * Firestore re-emits the live document whenever any field changes — a claim
   * banner, a pause timestamp — so this is called far more often than the
   * number actually changes.
   */
  announce(number: number | null | undefined): void {
    if (!this.enabled || number == null || number === this.lastSpoken) return;
    this.lastSpoken = number;

    const element = this.element ?? new Audio();
    this.element = element;
    element.src = `${CLIP_BASE}/${this.language}/${number}.mp3`;
    // A missing or slow clip must never break the game; the number is on
    // screen regardless.
    void element.play().catch(() => undefined);
  }

  reset(): void {
    this.lastSpoken = null;
  }
}

/** One-shot cues that may overlap speech. */
export function playCue(name: 'win' | 'gameover'): void {
  const audio = new Audio(`${CLIP_BASE}/${name}.mp3`);
  void audio.play().catch(() => undefined);
}
